import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';

export interface FilterDropdownOption {
    value: string;
    label: string;
    count?: number;
}

/**
 * Searchable single-select dropdown for long option lists (e.g. hundreds of collections).
 * Looks like the toolbar selects (Status / Type) and opens a popup styled like the
 * typeahead panel used in the Inventory filters: search box on top, scrollable list below.
 */
@Component({
    selector: 'app-filter-dropdown',
    templateUrl: './filter-dropdown.component.html',
    styleUrls: ['./filter-dropdown.component.scss'],
    standalone: false
})
export class FilterDropdownComponent implements OnDestroy {
    @Input() options: FilterDropdownOption[] = [];
    @Input() value = '';
    @Input() allLabel = 'All collections';
    @Input() searchPlaceholder = 'Search here..';
    @Input() emptyText = 'No results found.';
    /** Overrides the trigger text (e.g. "3 collections" when several are selected elsewhere). */
    @Input() displayLabel = '';
    @Output() valueChange = new EventEmitter<string>();

    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

    public open = false;
    public query = '';

    /**
     * Any click outside this dropdown closes it. Listened for in the capture phase so it still
     * fires when another control (a Bootstrap dropdown, a custom toggle) stops the click from
     * bubbling - otherwise two dropdowns could stay open together.
     */
    private readonly closeOnOutsideClick = (event: Event): void => {
        if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
            this.open = false;
        }
    };

    constructor(private host: ElementRef<HTMLElement>) {
        document.addEventListener('click', this.closeOnOutsideClick, true);
    }

    ngOnDestroy(): void {
        document.removeEventListener('click', this.closeOnOutsideClick, true);
    }

    get allOptions(): FilterDropdownOption[] {
        const list = this.options || [];
        return list.some((option) => !option.value) ? list : [{ value: '', label: this.allLabel }, ...list];
    }

    get filteredOptions(): FilterDropdownOption[] {
        const term = this.query.trim().toLowerCase();
        if (!term) return this.allOptions;
        return this.allOptions.filter((option) => option.value && (option.label || '').toLowerCase().includes(term));
    }

    get selectedLabel(): string {
        if (this.displayLabel) return this.displayLabel;
        const selected = this.allOptions.find((option) => option.value === (this.value || ''));
        if (!this.value) return selected?.label || this.allLabel;
        return selected?.label || this.value;
    }

    toggle(_event: Event): void {
        // The click is left to bubble so other open dropdowns (including Bootstrap ones) close.
        this.open = !this.open;
        if (this.open) {
            this.query = '';
            setTimeout(() => this.searchInput?.nativeElement.focus());
        }
    }

    select(option: FilterDropdownOption): void {
        this.open = false;
        if ((option.value || '') !== (this.value || '')) {
            this.valueChange.emit(option.value || '');
        }
    }

    trackByValue(_index: number, option: FilterDropdownOption): string {
        return option.value;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.open = false;
    }
}
